import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import Lenis from 'lenis';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { LanguageProvider, useLanguage } from '@/i18n/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Home from '@/pages/Home';
import Services from '@/pages/Services';
import About from '@/pages/About';
import Seasonal from '@/pages/Seasonal';
import Contact from '@/pages/Contact';
import Admin from '@/pages/Admin';
import { Privacy, Terms } from '@/pages/Legal';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

function MobileQuoteBar() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (pathname === '/contact' || pathname === '/admin') return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 90 }}
          animate={{ y: 0 }}
          exit={{ y: 90 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-ink/95 p-3 backdrop-blur md:hidden"
          data-testid="mobile-quote-bar"
        >
          <Link
            to="/contact"
            data-testid="mobile-quote-bar-button"
            className="flex w-full items-center justify-center gap-2 bg-[#C6A15B] py-3.5 font-display text-sm font-bold uppercase tracking-[0.18em] text-ink"
          >
            {t.nav.getQuote}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname === '/admin';

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <Header />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        <Route path="/seasonal" element={<Seasonal />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Home />} />
      </Routes>
      {!isAdmin && <Footer />}
      <MobileQuoteBar />
    </>
  );
}

function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <LanguageProvider>
          <Shell />
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
